import React, { useEffect } from "react";
import { Scrollbars } from "react-custom-scrollbars-4";
import { useSnackbar } from "notistack";
import {
  CircularProgress,
  Backdrop,
  Container,
  Stack,
  Tooltip,
  Toolbar,
  Typography,
  List,
  Breadcrumbs,
  Link,
  Fab,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SaveAs } from "@mui/icons-material";
// import { onEditDeleteAll } from "../helpers/onEditDeleteAll";
import ChecklistRow from "./ChecklistRow";
import { getProject } from "../helpers/getProject";
import { applyEditTrackingFields } from "../helpers/applyEditTrackingFields";
import { onEditSaveAll } from "../helpers/onEditSaveAll";
import { getSchema } from "../helpers/schemas/getSchema";
import { firestoreAutoId } from "../helpers/firestoreAutoId";
import { fileToBase64 } from "../helpers/fileToBase64";
import calculateFileSizeInBytes from "../helpers/calculateFileSizeInBytes";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import * as XLSX from "xlsx";
import { getUserAccess } from "../helpers/getUserAccess";
import ExportButton from "../Dialogs/ExportButton";

export default function Checklist(props) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [checklist, setChecklist] = React.useState([]);
  const [editFiles, setEditFiles] = React.useState([]);
  // const [equipment, setEquipment] = React.useState([]);
  // const [editFeature, setEditFeature] = React.useState(null);
  const [editSchema, setEditSchema] = React.useState([]);
  // const [formErrorMessage, setformErrorMessage] = React.useState("");
  const [project, setProject] = React.useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = window.location.href.split("/");
        const projId = params[params.length - 3];
        const checkListId = params[params.length - 1];
        const proj = await getProject(projId);
        setProject(proj);
        // setEquipment(proj.Equipment);
        const cklst = proj.Checklists.find((c) => c.id === checkListId);
        const files = proj.Files.filter((p) => p.checklist_id === checkListId);
        setEditSchema(getSchema("checklist"));
        setEditFiles([...files]);
        const selectedEquip = proj.Equipment.find(
          (c) => c.id === cklst.equipment_id,
        );
        cklst["Asset Name"] = selectedEquip
          ? selectedEquip.Name
          : cklst["Asset Name"];

        cklst.rows.forEach((r) => {
          if (!r.hasOwnProperty("value")) {
            r.value = "";
          }
        });
        setChecklist(cklst);
      } catch (error) {
        console.error("Error getting data:", error);
        enqueueSnackbar(error.message, {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoading === true && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  });

  const onSaveChecklist = async () => {
    try {
      setIsLoading(true);
      var chklt = applyEditTrackingFields(checklist, props.user);

      var readyFiles = [];
      for (var i = 0; i < editFiles.length; i++) {
        if (editFiles[i].file && editFiles[i].file.size !== undefined) {
          var fle = {};
          fle.id = editFiles[i].id;
          fle.comment = "";
          fle.file = await fileToBase64(editFiles[i].file);
          fle.name = editFiles[i].name;
          fle.deleted = false;
          fle.size = editFiles[i].file.size;
          fle["checklist_id"] = checklist.id;
          readyFiles.push(fle);
        }
      }

      //clean of sigs
      var sigFiles = chklt.rows.filter(
        (r) =>
          r["Row Type"] === "Signature" &&
          r.value.length > 0 &&
          r.value.startsWith("data:image/png;base64,"),
      );

      for (var z = 0; z < sigFiles.length; z++) {
        var sig = sigFiles[z];

        //check for old files that will be replaced
        for (var x = 0; x < editFiles.length; x++) {
          if (editFiles[x].id === sig.id) {
            editFiles[x].deleted = true;
            //this needs added to the ready files to be deleted
            readyFiles.push(editFiles[x]);
          }
        }
        //prep new file for upload
        var fl = {};
        fl.id = "new_" + sig.id;
        fl.comment = "";
        fl.file = sig.value;
        fl.name = firestoreAutoId() + ".png";
        fl.deleted = false;
        fl.size = calculateFileSizeInBytes(sig.value);
        fl["checklist_id"] = checklist.id;
        readyFiles.push(fl);
      }

      const savedProj = await onEditSaveAll(
        project,
        chklt,
        editSchema,
        readyFiles,
        "Checklists",
        "checklist_id",
        props.user,
      );

      setProject({ ...savedProj });
      const cklst = savedProj.Checklists.find((c) => c.id === checklist.id);
      setChecklist(cklst);
      setIsDirty(false);
      enqueueSnackbar("Checklists saved successfully", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "success",
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
      setIsLoading(false);
    }
  };

  const onChange = async (id, value) => {
    try {
      // console.log(id, value);
      var row = checklist.rows.find((r) => r.id === id);
      //Signature Row
      if (row["Row Type"] === "Signature" && value && value.current) {
        // Optional: get a trimmed canvas data URL to remove excess whitespace
        // const dataURL = signCanvas.current.getTrimmedCanvas().toDataURL('image/png');

        // Get the full canvas data URL
        row.value = value.current.toDataURL("image/png");

        console.log("Signature Data URL:", row.value); // Send this to your backend
      } else if (row["Row Type"] === "File") {
        if (value.size > 50000000) {
          throw Error(
            `Sorry, this file is over 50 megabytes: ${value.name} - File is to large.`,
          );
        }
        //delete existing file if it exists
        editFiles.forEach((f) => {
          if (f.id === row.id) {
            f.deleted = true;
          }
        });
        //check for existing new file
        var currentFile = editFiles.find((f) => f.id === "new_" + row.id);

        if (currentFile === undefined) {
          //add in new file
          var newFile = { id: "new_" + row.id };

          newFile.file = value;
          newFile.name = value.name;
          newFile.deleted = false;
          editFiles.push(newFile);
        } else {
          currentFile.file = value;
        }
        row.value = { name: value.name, size: value.size, url: "" };
      }
      //other Row types
      else if (row) {
        row.value = value;
        console.log(value);
      }
      setChecklist({ ...checklist });
      if (isDirty === false) {
        setIsDirty(true);
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  const onExportChecklist = () => {
    try {
      const dataToExport = checklist.rows.map((r) => {
        return {
          "Row Text": r["Row Text"],
          "Row Type": r["Row Type"],
          Value:
            r.value && r["Row Type"] === "File"
              ? r.value.name + " (" + r.value.url + ")"
              : r.value,
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, checklist.Name);
      XLSX.writeFile(
        workbook,
        `${project.Name}_Checklist_${checklist.Name}.xlsx`,
      );
    } catch (error) {
      console.error("Error exporting Checklist:", error);
      enqueueSnackbar(error.message, {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        variant: "error",
      });
    }
  };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <div style={{ width: "100%", height: "100%" }}>
      <Scrollbars
        autoHide
        autoHideTimeout={10}
        style={{ width: "100%", height: "100%" }}
      >
        <Toolbar />
        <Container
          maxWidth="xl"
          style={{
            // paddingTop: 30,
            paddingBottom: 30,
            // paddingBottom: theme.spacing(4),
            justifyContent: "center",
          }}
        >
          <Typography
            color="primary.light"
            style={{ paddingTop: 40 }}
            variant="h5"
            gutterBottom
          >
            {`Checklist: ${checklist.Name}`}
          </Typography>

          <Stack
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
            spacing={2}
          >
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link
                underline="hover"
                key="1"
                color="inherit"
                href=""
                onClick={() => navigate(`/project/${project.id}/checklists`)}
              >
                Checklists
              </Link>
              ,
              <Typography
                underline="hover"
                key="2"
                color="inherit"
                // href={`/project/${checklist.id}/issues`}

                // onClick={handleClick}
              >
                {`Checklist: (${checklist.Name})`}
              </Typography>
              ,
            </Breadcrumbs>
          </Stack>

          <Typography color="primary.dark" variant="h6" gutterBottom>
            {`Asset: ${checklist["Asset Name"]}`}
          </Typography>

          <Typography color="primary.dark" variant="subtitle1" gutterBottom>
            {`Checklist Type: ${checklist["Checklist Type"]}`}
          </Typography>
          <Typography color="primary.dark" variant="subtitle1" gutterBottom>
            {`Assigned to: ${checklist["Assigned to"]}`}
          </Typography>

          <Stack
            // style={{ paddingTop: 10 }}
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <ExportButton
              checklist={checklist}
              issue={null}
              observation={null}
              project={project}
              exportTableName="Checklist"
            />
            <Tooltip title="Export Checklist">
              <IconButton
                onClick={onExportChecklist}
                aria-label="Export Checklist"
                size="large"
                color="primary"
              >
                <CloudDownloadIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack
            // style={{ paddingTop: 10 }}
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          ></Stack>
          <Stack
            style={{ paddingTop: 10 }}
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <List style={{ width: "100%" }}>
              <ChecklistRow
                rows={checklist.rows}
                projectId={project.id}
                onChange={onChange}
              />
            </List>
          </Stack>
        </Container>
      </Scrollbars>
      <Tooltip title="Save Checklist">
        <Fab
          onClick={onSaveChecklist}
          aria-label="Save Checklist"
          size="medium"
          color="primary"
          disabled={
            isLoading ||
            !isDirty ||
            !getUserAccess(props.user, project.uid, project.Contacts)
              .allowUpdate
          }
          style={{
            position: "absolute",
            bottom: 16,
            right: 16,
          }}
        >
          <SaveAs />
        </Fab>
      </Tooltip>
    </div>
  );
}
